import { useState } from "react";
import { ExternalLink, Maximize2, ZoomIn, ZoomOut, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { drawingUrl, fileUrl } from "@/lib/api";
import { DRAWING_STATUS_LABELS, formatDate } from "@/lib/format";
import { StateChip, toneForDrawingStatus } from "@/components/common/Badges";

/* The SVG itself carries the "Eksempeltegning – ikke produksjonsgrunnlag" label and title block. */
export const DrawingFrame = ({ drawing, compact = false }) => (
  <div className="overflow-hidden rounded-md border bg-white" data-testid={`drawing-frame-${drawing.id}`}>
    <img
      src={drawingUrl(drawing.id)}
      alt={`${drawing.title} rev. ${drawing.code} – eksempeltegning`}
      className={`block w-full ${compact ? "max-h-[220px] object-cover object-left-top" : ""}`}
      loading="lazy"
    />
  </div>
);

export const DrawingViewer = ({ drawing, deliveryNumber }) => {
  const [open, setOpen] = useState(false);
  if (!drawing) return null;
  return (
    <div className="space-y-2">
      <button type="button" className="block w-full text-left" onClick={() => setOpen(true)} aria-label={`Åpne ${drawing.title}`}>
        <DrawingFrame drawing={drawing} />
      </button>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px]">
          <span className="font-medium">{drawing.title}</span>
          <span className="t-meta">Rev. {drawing.code}</span>
          <StateChip tone={toneForDrawingStatus(drawing.status)}>{DRAWING_STATUS_LABELS[drawing.status] || drawing.status}</StateChip>
          {drawing.approved_at && <span className="t-meta">Godkjent {formatDate(drawing.approved_at)} av {drawing.approved_by}</span>}
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="drawing-open-current-button"><Maximize2 /> Åpne tegning</Button>
          <Button variant="ghost" size="sm" asChild>
            <a href={drawingUrl(drawing.id)} target="_blank" rel="noreferrer" data-testid="drawing-open-new-tab"><ExternalLink /> Nytt vindu</a>
          </Button>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{drawing.title} · Rev. {drawing.code} {deliveryNumber && <span className="t-meta">· {deliveryNumber}</span>}</DialogTitle>
            <DialogDescription>{drawing.note || `${DRAWING_STATUS_LABELS[drawing.status] || drawing.status}`}</DialogDescription>
          </DialogHeader>
          <DrawingFrame drawing={drawing} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ZOOMS = [1, 1.5, 2, 3];

/**
 * One large viewer for the selected document in the register. Zoom, open in new window.
 * `item` is either a drawing ({type:'drawing', ...}) or a document ({type:'document', ...}).
 */
export const DocumentViewer = ({ item, testId = "document-viewer" }) => {
  const [zi, setZi] = useState(0);
  if (!item) {
    return <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed t-meta" data-testid={testId}>Velg et dokument i registeret.</div>;
  }
  const zoom = ZOOMS[zi];
  const isDrawing = item.type === "drawing";
  const url = isDrawing ? drawingUrl(item.id) : item.file_id ? fileUrl(item.file_id) : null;
  const isImage = isDrawing || item.content_type?.startsWith("image/");
  const isPdf = item.content_type === "application/pdf";
  return (
    <div className="frame overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-[14.5px] font-medium" data-testid="document-viewer-title">{item.title || item.name}{isDrawing && <span className="t-meta"> · Rev. {item.code}</span>}</p>
          <p className="t-meta truncate">{isDrawing ? "Forenklet tegning i demoen" : item.example ? "Dokument" : "Opplastet i demoøkten – utløper automatisk"}{item.created_at ? ` · ${formatDate(item.created_at)}` : ""}{item.created_by ? ` · ${item.created_by}` : ""}</p>
        </div>
        <div className="flex items-center gap-1">
          {isImage && (
            <>
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setZi(Math.max(0, zi - 1))} disabled={zi === 0} aria-label="Zoom ut" data-testid="viewer-zoom-out"><ZoomOut /></Button>
              <span className="w-12 text-center text-[12.5px] tabular text-muted-foreground">{Math.round(zoom * 100)} %</span>
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setZi(Math.min(ZOOMS.length - 1, zi + 1))} disabled={zi === ZOOMS.length - 1} aria-label="Zoom inn" data-testid="viewer-zoom-in"><ZoomIn /></Button>
            </>
          )}
          {url && (
            <Button variant="ghost" size="sm" asChild>
              <a href={url} target="_blank" rel="noreferrer" data-testid="viewer-open-new-tab"><Download /> Åpne / last ned</a>
            </Button>
          )}
        </div>
      </div>
      <div className="max-h-[70vh] overflow-auto bg-white">
        {item.body ? (
          <pre className="whitespace-pre-wrap p-4 font-mono text-[12.5px] leading-relaxed text-foreground" data-testid="document-body">{item.body}</pre>
        ) : url && isImage ? (
          <div style={{ width: `${zoom * 100}%` }}><img src={url} alt={item.title || item.name} className="block w-full" /></div>
        ) : url && isPdf ? (
          <iframe title={item.name} src={url} className="h-[70vh] w-full" />
        ) : url ? (
          <div className="p-6"><Button asChild variant="outline"><a href={url} target="_blank" rel="noreferrer">Åpne fil</a></Button></div>
        ) : (
          <p className="p-6 t-meta">Ingen forhåndsvisning tilgjengelig.</p>
        )}
      </div>
    </div>
  );
};

export const DocumentDialog = ({ doc, open, onOpenChange }) => {
  if (!doc) return null;
  const isImage = doc.content_type?.startsWith("image/");
  const isPdf = doc.content_type === "application/pdf";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{doc.name}</DialogTitle>
          <DialogDescription>
            {doc.example ? "Dokument" : "Opplastet i demoøkten – utløper automatisk"} · {formatDate(doc.created_at)} · {doc.created_by}
          </DialogDescription>
        </DialogHeader>
        {doc.body ? (
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-md border p-4 font-mono text-xs leading-relaxed surface-sunken" data-testid="document-body">{doc.body}</pre>
        ) : doc.file_id && isImage ? (
          <img src={fileUrl(doc.file_id)} alt={doc.name} className="max-h-[70vh] w-full rounded-md border object-contain" />
        ) : doc.file_id && isPdf ? (
          <iframe title={doc.name} src={fileUrl(doc.file_id)} className="h-[70vh] w-full rounded-md border" />
        ) : doc.file_id ? (
          <Button asChild variant="outline"><a href={fileUrl(doc.file_id)} target="_blank" rel="noreferrer">Åpne fil</a></Button>
        ) : (
          <p className="t-meta">Ingen forhåndsvisning tilgjengelig.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
